import { Pencil, Trash2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { View, XStack, YStack } from "tamagui";
import { PrimaryCheckText } from "../../../core/components/PrimaryCheck";
import { formatDate } from "../../../core/dayJs";
import { Permission } from "../../../permissions/types";
import { Box } from "../../../ui/Box";
import { BusinessModuleEditor } from "../../../ui/editor/useModuleEditor";
import { Pill, PillType } from "../../../ui/Pill";
import { Subtext } from "../../../ui/Subtext";
import { BusinessDoneStateUI } from "../ui/BusinessDoneDateUI";
import { BusinessDateDue } from "./BusinessLateDate";
import { BusinessReminderAdmin } from "./BusinessReminderAdmin";
import type DateDueModuleModel from "./BusinessReminderModuleModel";

export const BusinessReminderUI = observer(
  ({ businessModule }: { businessModule: DateDueModuleModel }) => {
    const { t } = useTranslation();
    const [showEdit, setShowEdit] = React.useState(false);

    const canEdit = businessModule.businessPlan.checkPermission(
      Permission.CanEditModules,
    );

    return (
      <YStack width="100%" gap="$space.medium">
        <View>
          {showEdit && (
            <BusinessReminderAdmin
              businessPlanStartingDate={
                businessModule.businessPlan.data.eventInfo.date
              }
              onSave={async (newData) => {
                setShowEdit(false);
                await businessModule.updateSettings(newData);
              }}
              data={businessModule.data}
            />
          )}
          <Box>
            <YStack
              flex={1}
              width="100%"
              marginTop="$space.small"
              gap={"$space.large"}
            >
              {!showEdit && (
                <>
                  <YStack gap="$small">
                    {businessModule.isCompletedByUserOrTeam ? (
                      <PrimaryCheckText label text={t("business:completed")} />
                    ) : (
                      <>
                        <XStack
                          flex={1}
                          gap={"$space.small"}
                          alignItems="center"
                        >
                          <BusinessDateDue
                            dateDue={businessModule.calculatedDate}
                          />
                        </XStack>
                        <Subtext>
                          {formatDate(
                            businessModule.calculatedDate
                              .toDate()
                              .toISOString(),
                          )}
                        </Subtext>
                      </>
                    )}
                  </YStack>
                  <BusinessModuleEditor
                    businessModule={businessModule}
                    hideEdit
                  />
                </>
              )}

              {canEdit && (
                <XStack gap="$space.small">
                  <Pill
                    type={PillType.Muted}
                    onPress={() => setShowEdit(!showEdit)}
                  >
                    <XStack
                      gap="$space.small"
                      alignItems="center"
                      cursor="pointer"
                    >
                      <Pencil size={16} />
                      <Subtext>{t("business:edit")}</Subtext>
                    </XStack>
                  </Pill>

                  <Pill
                    type={PillType.Muted}
                    onPress={() => {
                      businessModule.archive();
                    }}
                  >
                    <XStack
                      gap="$space.small"
                      alignItems="center"
                      cursor="pointer"
                    >
                      <Trash2 size={16} />
                      <Subtext>{t("business:delete")}</Subtext>
                    </XStack>
                  </Pill>
                </XStack>
              )}
            </YStack>
          </Box>

          <BusinessDoneStateUI businessModule={businessModule} />
        </View>
      </YStack>
    );
  },
);
